import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ListingAlertSubscription } from '../lib/listingAlertStorage';
import {
  getSavedListingAlerts,
  deleteSavedListingAlert,
} from '../lib/listingAlertStorage';

interface PropertyAlertContextType {
  isAlertModalOpen: boolean;
  alertModalParams: Partial<ListingAlertSubscription>;
  openAlertModal: (params?: Partial<ListingAlertSubscription>) => void;
  closeAlertModal: () => void;
  savedAlerts: ListingAlertSubscription[];
  refreshSavedAlerts: () => void;
  removeSavedAlert: (id: string) => void;
}

const PropertyAlertContext = createContext<PropertyAlertContextType | undefined>(undefined);

export const PropertyAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertModalParams, setAlertModalParams] = useState<Partial<ListingAlertSubscription>>({});
  const [savedAlerts, setSavedAlerts] = useState<ListingAlertSubscription[]>([]);

  const refreshSavedAlerts = useCallback(() => {
    setSavedAlerts(getSavedListingAlerts());
  }, []);

  useEffect(() => {
    refreshSavedAlerts();
  }, [refreshSavedAlerts]);

  const openAlertModal = useCallback((params: Partial<ListingAlertSubscription> = {}) => {
    setAlertModalParams(params);
    setIsAlertModalOpen(true);
  }, []);

  const closeAlertModal = useCallback(() => {
    setIsAlertModalOpen(false);
  }, []);

  const removeSavedAlert = useCallback((id: string) => {
    deleteSavedListingAlert(id);
    refreshSavedAlerts();
  }, [refreshSavedAlerts]);

  return (
    <PropertyAlertContext.Provider
      value={{
        isAlertModalOpen,
        alertModalParams,
        openAlertModal,
        closeAlertModal,
        savedAlerts,
        refreshSavedAlerts,
        removeSavedAlert,
      }}
    >
      {children}
    </PropertyAlertContext.Provider>
  );
};

export const usePropertyAlert = () => {
  const context = useContext(PropertyAlertContext);
  if (!context) {
    throw new Error('usePropertyAlert must be used within a PropertyAlertProvider');
  }
  return context;
};
